import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';

import { API_URL } from "../../config";

import { useAuth } from '../../App';
import { hasPermission } from "../../utils/permissions";
import Forbidden403 from "../../components/common/Forbidden403";

export default function JobTypesPage() {
  const { auth, fetchWithAuth } = useAuth();
  // Permission check: superuser or has 'job-types:list' permission
  const hasListPermission = hasPermission(auth?.user, "jobtype.list");
  if (!hasListPermission) {
    return <Forbidden403 />;
  }
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [jobTypeName, setJobTypeName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });


  const fetchJobTypes = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/job-types/`);
      if (!res.ok) throw new Error('Failed to fetch job types');
      const data = await res.json();
      setJobTypes(data);
    } catch (e) {
      setError(e.message);
      setJobTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobTypes();
  }, []);

  const handleOpenDialog = (jt = null) => {
    setDialogMode(jt ? 'edit' : 'create');
    setSelectedJobType(jt);
    setJobTypeName(jt ? jt.name : '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJobType(null);
    setJobTypeName('');
  };

  const handleSave = async () => {
    if (!jobTypeName.trim()) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      let res;
      if (dialogMode === 'create') {
        res = await fetchWithAuth(`${API_URL}/job-types/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: jobTypeName }),
        });
      } else {
        res = await fetchWithAuth(`${API_URL}/job-types/${selectedJobType.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: jobTypeName }),
        });
      }
      if (!res.ok) throw new Error('Failed to save job type');
      setSnackbar({ open: true, message: dialogMode === 'create' ? 'Job type created' : 'Job type updated', severity: 'success' });
      handleCloseDialog();
      fetchJobTypes();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jt) => {
    if (!window.confirm('Delete this job type?')) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/job-types/${jt.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete job type');
      setSnackbar({ open: true, message: 'Job type deleted', severity: 'success' });
      fetchJobTypes();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Manage Job Types</Typography>
      <Button variant="contained" startIcon={<Add />} sx={{ mb: 2 }} onClick={() => handleOpenDialog()}>Add Job Type</Button>
      {error && <Typography color="error">{error}</Typography>}
      <TableContainer component={Paper} sx={{ maxWidth: 600 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={2}><CircularProgress size={24} /></TableCell></TableRow>
            ) : jobTypes.length === 0 ? (
              <TableRow><TableCell colSpan={2}>No job types found</TableCell></TableRow>
            ) : (
              jobTypes.map((jt) => (
                <TableRow key={jt.id}>
                  <TableCell>{jt.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(jt)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(jt)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogMode === 'edit' ? 'Edit Job Type' : 'Add Job Type'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Job Type Name"
            value={jobTypeName}
            onChange={(e) => setJobTypeName(e.target.value)}
            fullWidth
            autoFocus
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">{dialogMode === 'edit' ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
